import React, { createContext, useContext, useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useSetCurrentUser } from '../slices/auth/hooks';
import { authMe } from '../slices/thunks';
import { initFirebaseBackend } from '../helpers/firebase_helper';
import Loading from '../Components/Loading';

// Create a context for Firebase Auth
const FirebaseAuthContext = createContext({
    isAuthenticated: false,
    isInitialized: false,
    user: null
});

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
    appId: process.env.REACT_APP_FIREBASE_APPID,
};

initFirebaseBackend(firebaseConfig);


export const useFirebaseAuth = () => useContext(FirebaseAuthContext);

export const FirebaseAuthProvider = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);

    const dispatch = useDispatch();
    const setCurrentUser = useSetCurrentUser();
    const navigate = useNavigate();

    const authMeData = async () => {
        try {
            const response = await dispatch(authMe());

            if (!response || response.error) {
                throw new Error(`Error getting document: ${response?.error || 'Unknown error'}`);
            }
            return response;
        } catch (error) {
            console.error('Authentication error:', error);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong with authentication!',
                icon: 'error',
                confirmButtonText: 'Ok',
            });
            throw error;
        }
    };

    useEffect(() => {
        console.log('Setting up Firebase auth state listener');
        const unsubscribe = firebase.auth().onAuthStateChanged((firebaseUser) => {
            const handleAuthStateChange = async () => {
                if (firebaseUser) {
                    console.log('User is signed in, fetching user data');
                    try {
                        const response = await authMeData();
                        if (response) {
                            setCurrentUser(response.payload);
                            setUser(response.payload);
                            setIsAuthenticated(true);
                            setIsInitialized(true);
                            console.log('User authenticated successfully');
                        } else {
                            console.log('No user found in database!');
                            await firebase.auth().signOut();
                            setCurrentUser(null);
                            setUser(null);
                            setIsAuthenticated(false);
                            setIsInitialized(true);
                        }
                    } catch (error) {
                        console.error('Error during authentication:', error);
                        await firebase.auth().signOut();
                        navigate('/login');
                        setCurrentUser(null);
                        setUser(null);
                        setIsAuthenticated(false);
                        setIsInitialized(true);
                    }
                } else {
                    console.log('No user is signed in');
                    setCurrentUser(null);
                    setUser(null);
                    setIsAuthenticated(false);
                    setIsInitialized(true);
                }
            };

            handleAuthStateChange();
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    const value = {
        isAuthenticated,
        isInitialized,
        user
    };

    if (!isInitialized) {
        return <Loading />
    }

    return (
        <FirebaseAuthContext.Provider value={value}>
            {children}
        </FirebaseAuthContext.Provider>
    );
};